import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { getAdminToken, isSuperUser } from "../components/adminAuth";
import { apiUrl } from "../lib/api";
import PaginationFooter from "../components/PaginationFooter";

const getErrorMessage = (error, fallback) => {
  const responseError = error?.response?.data?.error || error?.response?.data;
  if (!responseError) return fallback;
  if (typeof responseError === "string") return responseError;
  return responseError.message || JSON.stringify(responseError);
};

function DeleteExisting() {
  const canDelete = isSuperUser();
  const [items, setItems] = useState([]);
  const [query, setQuery] = useState("");
  const [deletingCode, setDeletingCode] = useState("");
  const [confirmCode, setConfirmCode] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const fetchEmployees = useCallback(async () => {
    const token = getAdminToken();
    const response = await axios.get(apiUrl("/api/employees"), {
      headers: token ? { Authorization: `Bearer ${token}` } : {}
    });
    setItems(response.data || []);
  }, []);

  useEffect(() => {
    fetchEmployees().catch((error) => console.error(error));
  }, [fetchEmployees]);

  const filteredItems = useMemo(
    () =>
      items.filter((item) =>
        `${item.employee_code} ${item.employee_name} ${item.department} ${item.designation}`
          .toLowerCase()
          .includes(query.toLowerCase())
      ),
    [items, query]
  );

  const totalPages = Math.max(1, Math.ceil(filteredItems.length / rowsPerPage));
  const paginatedItems = useMemo(() => {
    const startIndex = (currentPage - 1) * rowsPerPage;
    return filteredItems.slice(startIndex, startIndex + rowsPerPage);
  }, [currentPage, filteredItems, rowsPerPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [query, rowsPerPage]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const handleDelete = async (employeeCode) => {
    if (!canDelete) {
      alert("Only super user can delete employees.");
      return;
    }

    try {
      setDeletingCode(employeeCode);
      const token = getAdminToken();
      await axios.delete(apiUrl(`/api/employees/${employeeCode}`), {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      await fetchEmployees();
      setConfirmCode("");
    } catch (error) {
      console.error(error);
      alert(getErrorMessage(error, "Delete failed"));
    } finally {
      setDeletingCode("");
    }
  };

  if (!canDelete) {
    return (
      <section className="mx-auto w-full max-w-3xl">
        <div className="rounded-[28px] border border-white/80 bg-white/80 p-7 text-center shadow-[0_18px_50px_rgba(148,163,184,0.12)]">
          <h2 className="text-2xl font-bold text-slate-800">Permission Denied</h2>
          <p className="mt-2 text-sm text-slate-500">
            Only super user can delete employees. Please login with a super user account.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="mx-auto w-full max-w-6xl space-y-5 2xl:max-w-[90rem]">
      <div className="rounded-[28px] border border-white/80 bg-gradient-to-r from-[#f7f6fd] via-[#f8f5fb] to-[#efe5ff] p-5 shadow-[0_18px_50px_rgba(148,163,184,0.12)] md:p-7">
        <h2 className="text-2xl font-bold text-slate-800">Delete Employee</h2>
        <p className="mt-1 text-sm text-slate-500">
          Records shown below are live from database. Deletion is permanent.
        </p>
        <input
          type="text"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search by code, name, department, designation"
          className="mt-4 w-full rounded-full border border-[#ece9f8] bg-white/90 px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#ffc3cf]"
        />
      </div>

      <div className="w-full overflow-x-auto rounded-[28px] border border-white/80 bg-white/70 shadow-[0_16px_45px_rgba(148,163,184,0.12)] backdrop-blur-sm">
        <table className="w-full min-w-[900px] text-sm">
          <thead className="bg-[#f8f7fc] text-left text-slate-500">
            <tr>
              <th className="px-4 py-3 font-medium">Employee Code</th>
              <th className="px-4 py-3 font-medium">Employee Name</th>
              <th className="px-4 py-3 font-medium">Department</th>
              <th className="px-4 py-3 font-medium">Designation</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 font-medium">Action</th>
            </tr>
          </thead>
          <tbody>
            {filteredItems.length > 0 ? (
              paginatedItems.map((item) => (
                <tr key={item.employee_code} className="border-t border-[#efedf8]">
                  <td className="px-4 py-3 font-medium text-slate-800">{item.employee_code}</td>
                  <td className="px-4 py-3 text-slate-700">{item.employee_name}</td>
                  <td className="px-4 py-3 text-slate-700">{item.department}</td>
                  <td className="px-4 py-3 text-slate-700">{item.designation}</td>
                  <td className="px-4 py-3 text-slate-700">{item.status}</td>
                  <td className="px-4 py-3">
                    <button
                      type="button"
                      onClick={() => setConfirmCode(item.employee_code)}
                      disabled={deletingCode === item.employee_code}
                      className="rounded-full bg-gradient-to-r from-[#ff8c8c] to-[#ff6f91] px-3.5 py-2 text-xs font-semibold text-white shadow-sm hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {deletingCode === item.employee_code ? "Deleting..." : "Delete"}
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-slate-500">
                  No employee records found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <PaginationFooter
        currentPage={currentPage}
        totalItems={filteredItems.length}
        rowsPerPage={rowsPerPage}
        onPageChange={setCurrentPage}
        onRowsPerPageChange={setRowsPerPage}
      />

      {confirmCode && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-[#e6e2f4]/70 p-4 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-[28px] border border-white/80 bg-white/85 p-6 shadow-[0_18px_60px_rgba(148,163,184,0.18)] backdrop-blur-xl">
            <h3 className="text-lg font-semibold text-slate-900">Confirm Deletion</h3>
            <p className="mt-2 text-sm text-slate-600">
              Are you sure you want to delete employee <span className="font-semibold">{confirmCode}</span>?
            </p>
            <p className="mt-2 text-sm text-red-600">
              This will permanently remove employee details, and his/her birthday and anniversary will not be visible.
            </p>
            <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={() => setConfirmCode("")}
                className="rounded-full border border-white/80 bg-white/80 px-4 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-white"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => handleDelete(confirmCode)}
                disabled={deletingCode === confirmCode}
                className="rounded-full bg-gradient-to-r from-[#ff8c8c] to-[#ff6f91] px-4 py-2 text-sm font-semibold text-white shadow-sm hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {deletingCode === confirmCode ? "Deleting..." : "Delete Employee"}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

export default DeleteExisting;
